import { useState, useEffect, useMemo, useCallback } from 'react';
import { Pane, TextareaField, TextInput, Button, SelectMenu, toaster, Text, PlusIcon, RemoveIcon, ManuallyEnteredDataIcon } from 'evergreen-ui';
import FileUploaderMultipleUploadDemo from '../commons/FileUploaderMultipleUploadDemo';
import { toLocalSQLDatetime } from '../utils/timeUtils';
import { getGuests } from '../services/guestsServices';
import DiaryService from '../services/diaryService';

const emptyNote = () => ({ guest_id: '', guest_name: '', start_orario: '', end_orario: '', azione: '' });

const DiaryEntryForm = ({ onEntryAdded, userId, selectedDate}) => {
  const [testo, setTesto] = useState('');
  const [notes, setNotes] = useState([emptyNote()]);
  const [guests, setGuests] = useState([]);
  const [files, setFiles] = useState([]);

  const fetchGuestsData = useCallback(async () => {
    try {
      const g = await getGuests();
      const normalized = (g || []).map(guest => ({ ...guest, id: String(guest.id) }));
      setGuests(normalized);
    } catch (e) {
      console.error('Errore recupero ospiti', e);
    }
  }, []);

  useEffect(() => {
    fetchGuestsData();
  }, [fetchGuestsData]);

  const guestItems = useMemo(() => {
    const mapped = (guests || []).map(g => ({ label: `${g.nome} ${g.cognome}`, value: String(g.id) }));
    const items = mapped.concat([{ label: 'Tutti', value: 'tutti' }]);
    return items;
  }, [guests]);

  const addNoteRow = () => setNotes((s) => [...s, emptyNote()]);
  const removeNoteRow = (idx) => setNotes((s) => s.filter((_, i) => i !== idx));
  const updateNote = (idx, patch) => setNotes((s) => s.map((n, i) => (i === idx ? { ...n, ...patch } : n)));

  const handleFilesChange = (incoming) => setFiles(incoming);

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    
    const notesToSend = notes.filter(n => (n.guest_id && n.guest_id !== '') || n.azione);
    
    if (!testo.trim() && notesToSend.length === 0) {
      toaster.danger('Il testo principale o almeno una nota aggiuntiva sono richiesti');
      return;
    }

    for (let i = 0; i < notesToSend.length; i++) {
      const n = notesToSend[i];
      if (!n.start_orario || String(n.start_orario).trim() === '' || !n.end_orario || String(n.end_orario).trim() === '') {
        toaster.danger('Ogni nota aggiuntiva richiede un orario di inizio e di fine');
        return;
      }
    }

    try {
      const payload = {
        user_id: userId,
        testo,
        date: selectedDate ? toLocalSQLDatetime(selectedDate) : toLocalSQLDatetime(new Date()),
        notes: notesToSend.map(n => ({
          guest_id: n.guest_id === 'tutti' ? null : (n.guest_id || null),
          guest_name: n.guest_name || null,
          orario: n.start_orario || null,
          start_orario: n.start_orario || null,
          end_orario: n.end_orario || null,
          azione: n.azione || null
        })),
        files,
        entry_type: 'descrizione'
      };

      await DiaryService.addEntry(payload);
      toaster.success('Descrizione salvata');
      setTesto('');
      setNotes([emptyNote()]);
      setFiles([]);
      onEntryAdded && onEntryAdded();
    } catch (err) {
      console.error(err);
      toaster.danger('Errore durante il salvataggio');
    }
  };

  return (
    <Pane marginBottom={0} padding={0}>
      <form onSubmit={handleSubmit}>
        <TextareaField label="Descrizione della giornata" value={testo} onChange={e => setTesto(e.target.value)} inputHeight={160} flexibleHeight />

        <Pane marginY={8}>
          <Text>Note aggiuntive</Text>
          {notes.map((n, idx) => (
            <Pane key={idx} marginTop={8}>
              <Pane display="flex" gap={8} alignItems="center" style={{flexWrap:'wrap'}}>
                <SelectMenu
                  title="Seleziona ospite"
                  options={guestItems}
                  selected={n.guest_id ? [String(n.guest_id)] : []}
                  onSelect={item => updateNote(idx, { guest_id: item.value, guest_name: item.value === '' ? '' : item.label })}
                >
                  <Button type="button" style={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.guest_name || 'Seleziona un ospite'}</Button>
                </SelectMenu>
                <TextInput placeholder="Ora di inizio" inputProps={{ type: 'time' }} value={n.start_orario || ''} onChange={e => updateNote(idx, { start_orario: e.target.value })} width={120} />
                <TextInput placeholder="Ora di fine" inputProps={{ type: 'time' }} value={n.end_orario || ''} onChange={e => updateNote(idx, { end_orario: e.target.value })} width={120} />
              </Pane>
              <br></br>
              <TextareaField label="Azione svolta" value={n.azione} onChange={e => updateNote(idx, { azione: e.target.value })} flexibleHeight inputHeight={160} />
              <Pane display="flex" justifyContent="flex-end" marginTop={6}>
                <Button type="button" onClick={() => removeNoteRow(idx)} intent="danger" style={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Rimuovi <RemoveIcon marginLeft={8} /></Button>
              </Pane>
            </Pane>
          ))}
            <Button type="button" marginTop={8} onClick={addNoteRow} style={{ maxWidth: 220 }}>Aggiungi nota <PlusIcon marginLeft={8} /></Button>
        </Pane>

        <Pane marginY={8}>
          <FileUploaderMultipleUploadDemo onFilesChange={handleFilesChange} />
        </Pane>

        <Button type="submit" appearance="primary" intent="success" style={{ maxWidth: '100%', whiteSpace:'nowrap' }}>
          Inserisci descrizione <ManuallyEnteredDataIcon  marginLeft={8}/>
        </Button>
      </form>
    </Pane>
  );
};

export default DiaryEntryForm;
